// src/lib/naranjaPlusRules.ts
// ─────────────────────────────────────────────────────────────
// REGLAS PURAS DE "NARANJA PLUS" (suscripción cloud)
//
//   * Precio: $50.000 COP / mes (500 coins) — ver src/config/pricing.ts.
//   * Estado: ACTIVE · GRACE_PERIOD (margen tras vencer) · INACTIVE.
//   * Con la suscripción INACTIVA se apagan SOLO las funciones de internet
//     (nube, tienda digital pública, subastas). El POS, la facturación, las
//     mesas, el restaurante y el inventario local siguen al 100%.
//   * Los datos locales NUNCA se borran.
//
// Sin red, sin Dexie y sin UI: todo se puede probar de forma aislada.
// ─────────────────────────────────────────────────────────────

import { PLANS } from '@/config/pricing';

export const NARANJA_PLUS_PRICE_COP = PLANS.NARANJA_PLUS.cop;
export const NARANJA_PLUS_PRICE_COINS = PLANS.NARANJA_PLUS.coins;
export const NARANJA_PLUS_PERIOD_DAYS = 30;
/** Margen de gracia antes de apagar las funciones de internet. */
export const NARANJA_PLUS_GRACE_DAYS = 3;

/**
 * 🛠️ MODO DESARROLLO: todas las cuentas se consideran suscriptoras.
 * Vite reemplaza `import.meta.env.DEV` en build; en producción queda `false`
 * y la gobernanza real de Naranja Plus se aplica sin cambios.
 */
export const NARANJA_PLUS_DEV_OVERRIDE: boolean = (() => {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
})();

export type NaranjaStatus = 'ACTIVE' | 'INACTIVE' | 'GRACE_PERIOD';

export interface NaranjaPlusRow {
  business_id: string;
  status?: NaranjaStatus | string | null;
  expires_at?: string | null;
  monthly_price_cop?: number | string | null;
  auto_renew?: boolean | null;
  activated_at?: string | null;
  last_charge_at?: string | null;
  last_charge_coins?: number | string | null;
  cancel_reason?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface NaranjaPlusState {
  status: NaranjaStatus;
  /** Días restantes (0 si ya venció). */
  daysLeft: number;
  /** Suscripción vigente (habilita las funciones de internet). */
  isActive: boolean;
  /** Está en margen de gracia (vencida pero aún tolerada). */
  isGrace: boolean;
  priceCop: number;
  autoRenew: boolean;
  expiresAt: string | null;
  /** Texto del badge de la cabecera. */
  badge: string;
}

const toMs = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Precio mensual de la fila (o el oficial). */
export const naranjaPriceCop = (row?: NaranjaPlusRow | null): number => {
  const value = Number(row?.monthly_price_cop ?? 0);
  return value > 0 ? value : NARANJA_PLUS_PRICE_COP;
};

/** Convierte el precio mensual a monedas Flip (1 coin = coinValueCOP). */
/**
 * Convierte el precio mensual a monedas Flip.
 * 1 Flip Coin = $100 COP → Naranja Plus ($35.000 COP) = 350 coins.
 */
export const naranjaCoinsForMonth = (row?: NaranjaPlusRow | null, coinValueCOP = 100): number =>
  Math.max(1, Math.ceil(naranjaPriceCop(row) / (Number(coinValueCOP) > 0 ? Number(coinValueCOP) : 100)));

/** Nuevo vencimiento tras pagar (extiende desde hoy o desde el vencimiento vigente). */
export const nextExpiryFrom = (
  current?: string | null,
  now = new Date(),
  days = NARANJA_PLUS_PERIOD_DAYS,
): string => {
  const currentMs = toMs(current);
  const base = currentMs && currentMs > now.getTime() ? currentMs : now.getTime();
  return new Date(base + days * DAY_MS).toISOString();
};

/**
 * ESTADO COMPLETO de la suscripción: activo, en gracia o inactivo, con los días
 * restantes y el texto del badge.
 */
export const computeNaranjaState = (row?: NaranjaPlusRow | null, now = Date.now()): NaranjaPlusState => {
  const expiresAt = row?.expires_at || null;
  const expiresMs = toMs(expiresAt);
  const priceCop = naranjaPriceCop(row);
  const autoRenew = !!row?.auto_renew;
  const graceMs = NARANJA_PLUS_GRACE_DAYS * DAY_MS;

  // 🛠️ En desarrollo, cualquier negocio opera como suscriptor activo.
  if (NARANJA_PLUS_DEV_OVERRIDE) {
    return {
      status: 'ACTIVE',
      daysLeft: 3650,
      isActive: true,
      isGrace: false,
      priceCop,
      autoRenew: true,
      expiresAt: '2099-12-31T23:59:59.999Z',
      badge: '🍊 Naranja Plus: Activo (modo desarrollo)',
    };
  }

  const declared = String(row?.status || 'INACTIVE').toUpperCase() as NaranjaStatus;
  const neverActivated = !expiresMs;

  let status: NaranjaStatus;
  if (neverActivated || declared === 'INACTIVE') {
    status = 'INACTIVE';
  } else if (expiresMs > now) {
    status = 'ACTIVE';
  } else if (expiresMs + graceMs > now) {
    status = 'GRACE_PERIOD';
  } else {
    status = 'INACTIVE';
  }

  const daysLeft = expiresMs ? Math.max(0, Math.ceil((expiresMs - now) / DAY_MS)) : 0;
  const isActive = status === 'ACTIVE' || status === 'GRACE_PERIOD';

  const badge = !isActive
    ? '🍊 Naranja Plus: Inactivo'
    : status === 'GRACE_PERIOD'
      ? `🍊 Naranja Plus: Gracia (venció hace ${Math.abs(daysLeft)} día(s))`
      : `🍊 Naranja Plus: Activo (vence en ${daysLeft} día${daysLeft === 1 ? '' : 's'})`;

  return { status, daysLeft, isActive, isGrace: status === 'GRACE_PERIOD', priceCop, autoRenew, expiresAt, badge };
};

/** ¿Hay que renovar automáticamente? (ACTIVE/GRACE, auto_renew y saldo suficiente) */
export const shouldAutoRenew = (
  row: NaranjaPlusRow | null | undefined,
  flipBalance: number,
  coinValueCOP = 100,
  now = Date.now(),
): boolean => {
  const state = computeNaranjaPlusSafe(row, now);
  if (!state.autoRenew) return false;
  // Renueva cuando quedan 3 días o menos (o si venció).
  if (state.status === 'INACTIVE') return false;
  if (state.daysLeft > 3) return false;
  return flipBalance >= naranjaCoinsForMonth(row, coinValueCOP);
};

const computeNaranjaPlusSafe = (row: NaranjaPlusRow | null | undefined, now: number) =>
  computeNaranjaState(row, now);

/** Campos a guardar al activar/renovar. */
export const buildActivationUpdate = (
  row: NaranjaPlusRow | null | undefined,
  coins: number,
  now = new Date(),
): Partial<NaranjaPlusRow> => ({
  status: 'ACTIVE',
  expires_at: nextExpiryFrom(row?.expires_at, now),
  activated_at: row?.activated_at || now.toISOString(),
  last_charge_at: now.toISOString(),
  last_charge_coins: coins,
  cancel_reason: null,
  updated_at: now.toISOString(),
});

/** Campos al vencer sin saldo (no borra nada, solo marca el estado). */
export const buildInactiveUpdate = (row: NaranjaPlusRow | null | undefined, reason: string, now = new Date()): Partial<NaranjaPlusRow> => ({
  status: 'INACTIVE',
  cancel_reason: reason,
  updated_at: now.toISOString(),
  ...(row?.expires_at ? {} : { expires_at: null }),
});

/** ¿Esta función de internet está disponible? */
export const isCloudFeatureAllowed = (state: NaranjaPlusState): boolean => state.isActive;

/** Texto único del bloqueo para tienda/subastas/nube. */
export const NARANJA_REQUIRED_MESSAGE = 'Requiere suscripción Naranja Plus activa';

/** Banner de la tienda digital pública cuando la suscripción está inactiva. */
export const STORE_CLOSED_TITLE = 'Negocio cerrado temporalmente';
export const STORE_CLOSED_DETAIL =
  'Este negocio no tiene activa su suscripción Naranja Plus. Vuelve pronto o contacta al negocio por WhatsApp.';
